import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ArrowRight, Check } from 'lucide-react';

export const INPUT_STYLE = {
  background: 'rgba(255,255,255,0.025)',
  border: '1px solid var(--b1)',
  color: 'var(--white)',
};

export const CARD_STYLE = {
  background: 'var(--l1)',
  border: '1px solid var(--b1)',
  boxShadow: '0 1px 0 rgba(255,255,255,0.02) inset',
};

export function Field({ label, hint, children, required, testId }) {
  return (
    <div data-testid={testId}>
      <label className="font-body text-xs mb-1 block" style={{ color: 'var(--ts)' }}>
        {label}{required && <span style={{ color: 'var(--a)' }}> *</span>}
      </label>
      {children}
      {hint && <p className="font-body text-[11px] mt-1" style={{ color: 'var(--te)' }}>{hint}</p>}
    </div>
  );
}

export function TextField({ label, value, onChange, placeholder, type = 'text', required, testId, maxLength }) {
  return (
    <Field label={label} required={required} testId={testId}>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="font-body text-sm h-11 rounded-[3px]"
        style={INPUT_STYLE}
        required={required}
        maxLength={maxLength}
      />
    </Field>
  );
}

export function SelectField({ label, value, onChange, options, required, testId, placeholder = '— select —' }) {
  return (
    <Field label={label} required={required} testId={testId}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full font-body text-sm h-11 rounded-[3px] px-3"
        style={INPUT_STYLE}
        required={required}
      >
        <option value="" style={{ background: 'var(--l1)' }}>{placeholder}</option>
        {options.map((o) => {
          const v = typeof o === 'string' ? o : o.value;
          const l = typeof o === 'string' ? o : o.label;
          return <option key={v} value={v} style={{ background: 'var(--l1)' }}>{l}</option>;
        })}
      </select>
    </Field>
  );
}

export function TextareaField({ label, value, onChange, placeholder, rows = 4, testId, hint }) {
  return (
    <Field label={label} hint={hint} testId={testId}>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className="w-full font-body text-sm rounded-[3px] p-3 resize-none"
        style={INPUT_STYLE}
      />
    </Field>
  );
}

export function RadioField({ label, value, onChange, options, required, testId, columns = 1 }) {
  const cols = columns === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1';
  return (
    <Field label={label} required={required} testId={testId}>
      <div className={`grid ${cols} gap-2`}>
        {options.map((o) => {
          const v = typeof o === 'string' ? o : o.value;
          const l = typeof o === 'string' ? o : o.label;
          const isSelected = value === v;
          return (
            <label
              key={v}
              className="flex items-center gap-3 p-3 rounded-[3px] cursor-pointer transition-all"
              style={{
                background: isSelected ? 'var(--a-glow)' : 'rgba(255,255,255,0.02)',
                border: `1px solid ${isSelected ? 'var(--a)' : 'var(--b1)'}`,
              }}
            >
              <span
                className="flex items-center justify-center rounded-full flex-shrink-0"
                style={{
                  width: 16,
                  height: 16,
                  border: `1px solid ${isSelected ? 'var(--a)' : 'var(--b2)'}`,
                  background: isSelected ? 'var(--a)' : 'transparent',
                }}
              >
                {isSelected && <span className="block rounded-full" style={{ width: 6, height: 6, background: 'var(--white)' }} />}
              </span>
              <span className="font-body text-sm" style={{ color: 'var(--white)' }}>{l}</span>
              <input
                type="radio"
                checked={isSelected}
                onChange={() => onChange(v)}
                className="sr-only"
              />
            </label>
          );
        })}
      </div>
    </Field>
  );
}

export function CheckboxField({ label, values, onChange, options, testId, required }) {
  const toggle = (v) => {
    const next = values.includes(v) ? values.filter((x) => x !== v) : [...values, v];
    onChange(next);
  };
  return (
    <Field label={label} required={required} testId={testId}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {options.map((o) => {
          const v = typeof o === 'string' ? o : o.value;
          const l = typeof o === 'string' ? o : o.label;
          const isSelected = values.includes(v);
          return (
            <label
              key={v}
              className="flex items-center gap-3 p-3 rounded-[3px] cursor-pointer transition-all"
              style={{
                background: isSelected ? 'var(--a-glow)' : 'rgba(255,255,255,0.02)',
                border: `1px solid ${isSelected ? 'var(--a)' : 'var(--b1)'}`,
              }}
            >
              <span
                className="flex items-center justify-center flex-shrink-0 rounded-[3px]"
                style={{
                  width: 16,
                  height: 16,
                  border: `1px solid ${isSelected ? 'var(--a)' : 'var(--b2)'}`,
                  background: isSelected ? 'var(--a)' : 'transparent',
                  color: 'var(--white)',
                }}
              >
                {isSelected && <Check size={11} />}
              </span>
              <span className="font-body text-sm" style={{ color: 'var(--white)' }}>{l}</span>
              <input type="checkbox" checked={isSelected} onChange={() => toggle(v)} className="sr-only" />
            </label>
          );
        })}
      </div>
    </Field>
  );
}

export function PrimaryButton({ children, onClick, disabled, type = 'button', testId }) {
  return (
    <Button
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className="btn-shimmer font-body text-sm h-11 px-6 rounded-[3px]"
      style={{ background: 'var(--a)', color: 'var(--white)', opacity: disabled ? 0.5 : 1 }}
    >
      {children}
    </Button>
  );
}

export function NextButton({ disabled, onClick, label = 'Continue', testId }) {
  return (
    <div className="flex justify-end mt-6">
      <PrimaryButton onClick={onClick} disabled={disabled} testId={testId}>
        {label} <ArrowRight size={14} className="ml-2" />
      </PrimaryButton>
    </div>
  );
}

/** Render a price row from /api/public/service-pricing with consistent fallback copy. */
export function formatServicePrice(row) {
  if (!row) return 'Price on consultation';
  if (!row.price || Number(row.price) === 0) return row.notes || 'Price on consultation';
  const gbp = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0,
  }).format(Number(row.price));
  return row.per_month ? `${gbp}/month` : gbp;
}

export function formatCoursePrice(value) {
  if (!value || Number(value) === 0) return 'Price on consultation';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0,
  }).format(Number(value));
}
